import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { httpGetPumpEvents, httpAcknowledgeComment } from 'services/pumps';
import { useAuthContext } from 'contexts/AuthContext';
import { PUMP_STATUS_COLOR_CLASS, pumpStatusTranslationKey } from './pumpStatusMeta';
import './PumpEventsModal.scss';

export default function PumpEventsModal({ isOpen, pumpId, onClose }) {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledgingEventId, setAcknowledgingEventId] = useState(null);
  const { myUser } = useAuthContext();
  const locale = i18n.language === 'en' ? 'en-GB' : 'fi-FI';

  useEffect(() => {
    if (!isOpen || !pumpId) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpGetPumpEvents(pumpId);
        setEvents(Array.isArray(response.data) ? response.data : response.data?.results || []);
      } catch (err) {
        console.error('Error fetching pump events:', err);
        setError(t('events_modal_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [isOpen, pumpId, t]);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })} (${date.toLocaleDateString(locale)})`;
  };

  const label = (code) => t(pumpStatusTranslationKey(code));

  const getEventDescription = (event) => {
    if (event.event_type === 6) {
      return t('events_comment');
    }

    if (event.from_status && event.to_status) {
      const fromStatus = label(event.from_status) || t('events_unknown');
      const toStatus = label(event.to_status) || t('events_unknown');
      let description = t('events_status_change', { from: fromStatus, to: toStatus });

      if (event.from_pool_name && event.to_pool_name && event.from_pool_name !== event.to_pool_name) {
        description += ` (${event.from_pool_name} → ${event.to_pool_name})`;
      } else if (event.to_pool_name) {
        description += ` (${event.to_pool_name})`;
      }

      return description;
    }

    return t('events_generic');
  };

  const handleAcknowledge = async (eventId) => {
    try {
      setAcknowledgingEventId(eventId);
      await httpAcknowledgeComment(eventId);

      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventId ? { ...event, is_acknowledged_by_current_user: true } : event
        )
      );
    } catch (err) {
      console.error('Error acknowledging comment:', err);
      alert(t('events_ack_error'));
    } finally {
      setAcknowledgingEventId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('events_modal_title')} size="large">
      {loading ? (
        <div className="text-center py-5">{t('events_modal_loading')}</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : events.length === 0 ? (
        <div className="text-center py-5 text-muted">{t('events_modal_empty')}</div>
      ) : (
        <div className="events-list">
          {events.map((event, idx) => {
            const isOwnComment = event.user === myUser?.id;
            const requiresAck =
              event.requires_acknowledgement && !event.is_acknowledged_by_current_user && !isOwnComment;
            const showAckButton = requiresAck;

            return (
              <div key={idx} className={`event-item ${requiresAck ? 'requires-acknowledgement' : ''}`}>
                <div className="event-header">
                  <div className="event-time">
                    {requiresAck && <i className="fas fa-exclamation-circle text-danger me-2" />}
                    {formatDateTime(event.timestamp)}
                  </div>
                  {event.to_status && (
                    <div className={`event-status ${PUMP_STATUS_COLOR_CLASS[event.to_status] || ''}`}>
                      {label(event.to_status)}
                    </div>
                  )}
                </div>
                <div className="event-description">{getEventDescription(event)}</div>
                {event.comment && (
                  <div className="event-comment">
                    <i className="fas fa-comment-alt" /> {event.comment}
                  </div>
                )}
                {event.user_name && (
                  <div className="event-user">
                    <i className="fas fa-user" /> {event.user_name}
                  </div>
                )}
                {showAckButton && (
                  <div className="event-actions mt-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleAcknowledge(event.id)}
                      disabled={acknowledgingEventId === event.id}
                    >
                      {acknowledgingEventId === event.id ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                          {t('events_ack_loading')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-1" />
                          {t('events_ack_btn')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
